'use client'
import type { Form as FormType } from '@/payload-types'

import { useRouter } from 'next/navigation'
import React, { useCallback, useRef, useState } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import RichText from '@onclicks/sharedcomponents/RichText'
import {
  PayloadRecaptcha,
  type RecaptchaHandle,
} from '@onclicks/recaptcha/components/PayloadRecaptcha'
import { Button } from './ui/Button'
import { Icon } from './Icon'

import { fields } from './FieldForm/fields'
import { getClientSideURL } from '@/utilities/getURL'
import clsx from 'clsx'

export type NewsletterBlockType = {
  form: FormType
}

export const NewsletterComponent: React.FC<NewsletterBlockType> = (props) => {
  const {
    form: formFromProps,
    form: { id: formID, confirmationMessage, confirmationType, redirect, submitButtonLabel } = {},
  } = props
  const requireRecaptcha = (formFromProps as FormType & { requireRecaptcha?: boolean })
    .requireRecaptcha

  const recaptcha = useRef<RecaptchaHandle>(null)

  const formMethods = useForm({
    defaultValues: formFromProps.fields as unknown as Record<string, string>,
  })
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
  } = formMethods

  const [isLoading, setIsLoading] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState<boolean>()
  const [error, setError] = useState<{ message: string; status?: string } | undefined>()
  const router = useRouter()

  const onSubmit = useCallback(
    (data: Record<string, string>) => {
      let loadingTimerID: ReturnType<typeof setTimeout>
      const submitForm = async () => {
        setError(undefined)

        const captchaValue = recaptcha.current ? recaptcha.current.getValue() : undefined

        if (requireRecaptcha && !captchaValue) {
          setIsLoading(false)
          setError({
            message: 'Veuillez valider le captcha.',
            status: '400',
          })
          return
        }

        const dataToSend = Object.entries(data).map(([name, value]) => ({
          field: name,
          value,
        }))

        // delay loading indicator by 1s
        loadingTimerID = setTimeout(() => {
          setIsLoading(true)
        }, 1000)

        try {
          const req = await fetch(`${getClientSideURL()}/api/form-submissions`, {
            body: JSON.stringify({
              form: formID,
              submissionData: dataToSend,
              recaptcha: captchaValue,
            }),
            headers: {
              'Content-Type': 'application/json',
            },
            method: 'POST',
          })

          const res = await req.json()

          clearTimeout(loadingTimerID)

          if (req.status >= 400) {
            setIsLoading(false)

            setError({
              message: res.errors?.[0]?.message || 'Internal Server Error',
              status: res.status,
            })
            recaptcha.current?.reset?.()

            return
          }

          setIsLoading(false)
          setHasSubmitted(true)
          recaptcha.current?.reset?.()

          if (confirmationType === 'redirect' && redirect) {
            const { url } = redirect

            const redirectUrl = url

            if (redirectUrl) router.push(redirectUrl)
          }
        } catch (err) {
          console.warn(err)
          setIsLoading(false)
          setError({
            message: 'Something went wrong.',
          })
          recaptcha.current?.reset?.()
        }
      }

      void submitForm()
    },
    [router, formID, redirect, confirmationType, requireRecaptcha],
  )

  return (
    <>
      <div className={clsx('w-full')}>
        <FormProvider {...formMethods}>
          {!isLoading && hasSubmitted && confirmationType === 'message' && confirmationMessage && (
            <RichText data={confirmationMessage} enableGutter={false} enableProse={false} />
          )}
          {isLoading && !hasSubmitted && (
            <p className="text-sm text-primary/70">Chargement, veuillez patienter...</p>
          )}
          {error && (
            <div className="text-sm text-red-600">{`${error.status || '500'}: ${error.message || ''}`}</div>
          )}
          {!hasSubmitted && (
            <form
              id={formID?.toString()}
              onSubmit={handleSubmit(onSubmit)}
              className={clsx('flex flex-col gap-3 w-full')}
            >
              {formFromProps &&
                formFromProps.fields &&
                formFromProps.fields?.map((field, index) => {
                  const Field: React.FC<any> = fields?.[field.blockType as keyof typeof fields]
                  if (Field) {
                    return (
                      <div key={index} className={clsx('w-full [&_label]:sr-only')}>
                        <Field
                          key={index}
                          form={formFromProps}
                          {...field}
                          {...formMethods}
                          control={control}
                          errors={errors}
                          register={register}
                        />
                      </div>
                    )
                  }
                  return null
                })}

              <PayloadRecaptcha
                ref={recaptcha}
                requireRecaptcha={requireRecaptcha}
                className="origin-top-left scale-[0.75] -mb-6"
              />

              <Button
                form={formID?.toString()}
                className="w-full justify-center gap-2  text-sm"
                type="submit"
                variant="gradientPrimaryToPink"
              >
                {submitButtonLabel}
                <Icon reactIconsIcon="paper-plane" className="min-w-0! pt-0!" />
              </Button>
            </form>
          )}
        </FormProvider>
      </div>
    </>
  )
}
